import { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Calendar,
  User,
  Briefcase,
  Download,
  ArrowLeft,
  AlertCircle,
  LogOut,
  Moon,
  Sun,
  Settings,
  Image as ImageIcon
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';

interface Timesheet {
  id: string;
  consultant_id: string;
  week_ending: string;
  total_hours: number;
  status: string;
  notes: string;
  submitted_at: string;
  project_name: string;
  daily_hours: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
}

export default function ApproveTime() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const { user, signOut } = useAuth();
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'approved' | 'rejected'>('submitted');
  const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null);

  useEffect(() => {
    fetchTimesheets();
  }, [user]);

  const fetchTimesheets = async () => {
    try {
      const { data, error } = await supabase
        .from('timesheets')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      setTimesheets(data || []);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (timesheetId: string) => {
    try {
      const { error } = await supabase
        .from('timesheets')
        .update({ status: 'approved' })
        .eq('id', timesheetId);

      if (error) throw error;

      await supabase.rpc('create_notification', {
        p_user_id: timesheets.find(t => t.id === timesheetId)?.consultant_id,
        p_title: 'Timesheet Approved',
        p_message: `Your timesheet for week ending ${timesheets.find(t => t.id === timesheetId)?.week_ending} has been approved.`,
        p_type: 'success',
        p_category: 'assignment',
        p_link: '/consultant/timesheet'
      });

      fetchTimesheets();
      setSelectedTimesheet(null);
    } catch (error) {
      console.error('Error approving timesheet:', error);
    }
  };

  const handleReject = async (timesheetId: string) => {
    try {
      const { error } = await supabase
        .from('timesheets')
        .update({ status: 'rejected' })
        .eq('id', timesheetId);

      if (error) throw error;

      await supabase.rpc('create_notification', {
        p_user_id: timesheets.find(t => t.id === timesheetId)?.consultant_id,
        p_title: 'Timesheet Rejected',
        p_message: `Your timesheet for week ending ${timesheets.find(t => t.id === timesheetId)?.week_ending} needs revision. Please review and resubmit.`,
        p_type: 'warning',
        p_category: 'assignment',
        p_link: '/consultant/timesheet'
      });

      fetchTimesheets();
      setSelectedTimesheet(null);
    } catch (error) {
      console.error('Error rejecting timesheet:', error);
    }
  };

  const getFilteredTimesheets = () => {
    let filtered = timesheets;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'submitted':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const stats = {
    pending: timesheets.filter(t => t.status === 'submitted').length,
    approved: timesheets.filter(t => t.status === 'approved').length,
    rejected: timesheets.filter(t => t.status === 'rejected').length,
    totalHours: timesheets
      .filter(t => t.status === 'approved')
      .reduce((sum, t) => sum + (t.total_hours || 0), 0)
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await signOut();
    navigate('/customer/login');
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <WorkdayClientHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-8">
        <button
          onClick={() => navigate('/workday-client/dashboard')}
          className="mb-6 flex items-center text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Approve Time</h1>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Review and approve consultant timesheets
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Pending Review
              </h3>
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold">{stats.pending}</p>
          </div>

          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Approved
              </h3>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold">{stats.approved}</p>
          </div>

          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Rejected
              </h3>
              <XCircle className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-3xl font-bold">{stats.rejected}</p>
          </div>

          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Hours
              </h3>
              <Briefcase className="w-5 h-5 text-[#40E0D0]" />
            </div>
            <p className="text-3xl font-bold">{stats.totalHours}</p>
          </div>
        </div>

        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('submitted')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'submitted'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending ({stats.pending})
              </button>
              <button
                onClick={() => setStatusFilter('approved')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'approved'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Approved ({stats.approved})
              </button>
              <button
                onClick={() => setStatusFilter('rejected')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'rejected'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Rejected ({stats.rejected})
              </button>
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search timesheets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent`}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {getFilteredTimesheets().length === 0 ? (
            <div className={`text-center py-12 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
              <Clock className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold mb-2">No timesheets found</h3>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {statusFilter === 'submitted' && 'No timesheets pending approval'}
                {statusFilter === 'approved' && 'No approved timesheets yet'}
                {statusFilter === 'rejected' && 'No rejected timesheets'}
                {statusFilter === 'all' && 'No timesheets submitted yet'}
              </p>
            </div>
          ) : (
            getFilteredTimesheets().map((timesheet) => (
              <div
                key={timesheet.id}
                className={`p-6 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'
                } shadow-sm hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {getStatusIcon(timesheet.status)}
                      <h3 className="text-lg font-semibold">
                        Week Ending: {new Date(timesheet.week_ending).toLocaleDateString()}
                      </h3>
                      <span className={`text-xs px-3 py-1 rounded-full border ${getStatusColor(timesheet.status)}`}>
                        {timesheet.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="w-4 h-4 text-[#40E0D0]" />
                        <span>{timesheet.project_name || 'Project Name'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-[#40E0D0]" />
                        <span>{timesheet.total_hours} hours</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-[#40E0D0]" />
                        <span>Submitted: {new Date(timesheet.submitted_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {timesheet.notes && (
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                        Notes: {timesheet.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {timesheet.status === 'submitted' && (
                      <>
                        <button
                          onClick={() => handleApprove(timesheet.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(timesheet.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                        >
                          <XCircle className="w-5 h-5" />
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setSelectedTimesheet(timesheet)}
                      className="px-4 py-2 border-2 border-[#40E0D0] text-[#40E0D0] rounded-lg hover:bg-[#40E0D0] hover:text-black transition-colors font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {selectedTimesheet && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-3xl w-full rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Timesheet Details</h2>
                <button
                  onClick={() => setSelectedTimesheet(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Week Ending</label>
                    <p className="text-lg">{new Date(selectedTimesheet.week_ending).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p className="text-lg">
                      <span className={`px-3 py-1 rounded-full text-sm border ${getStatusColor(selectedTimesheet.status)}`}>
                        {selectedTimesheet.status}
                      </span>
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 mb-3 block">Daily Hours</label>
                  <div className={`rounded-lg overflow-hidden border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
                    <table className="w-full">
                      <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium">Day</th>
                          <th className="px-4 py-2 text-right text-sm font-medium">Hours</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedTimesheet.daily_hours && Object.entries(selectedTimesheet.daily_hours).map(([day, hours]) => (
                          <tr key={day}>
                            <td className="px-4 py-2 capitalize">{day}</td>
                            <td className="px-4 py-2 text-right font-semibold">{hours}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                        <tr>
                          <td className="px-4 py-2 font-semibold">Total</td>
                          <td className="px-4 py-2 text-right font-bold text-[#40E0D0]">{selectedTimesheet.total_hours} hours</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {selectedTimesheet.notes && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Notes</label>
                    <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {selectedTimesheet.notes}
                    </p>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  {selectedTimesheet.status === 'submitted' && (
                    <>
                      <button
                        onClick={() => handleApprove(selectedTimesheet.id)}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(selectedTimesheet.id)}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-5 h-5" />
                        Reject
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setSelectedTimesheet(null)}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
